import React, { useCallback, useEffect, useState } from "react";
import { Loader2, MapPinOff } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { RDV_CHANGED, TYPE_COLOR } from "../../lib/planning";
import PlanningMap from "./PlanningMap";
import TourneePanel from "./TourneePanel";

const LEGEND = [["pose", "Pose à planifier"], ["sav", "SAV sans RDV"], ["desinstallation", "Désinstallation à planifier"]];

const matches = (p, f) => {
  const types = [...p.a_planifier.map((a) => a.type), ...p.rdvs.map((r) => r.type)];
  return (!f.type || types.includes(f.type)) && (!f.status || p.rdvs.some((r) => r.status === f.status));
};

export default function PlanningMapView({ opts, filters, onOpenRdv }) {
  const [scope, setScope] = useState("a_planifier");
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [route, setRoute] = useState(null);
  const load = useCallback(() => { setData(null); api.get("/planning/map", { params: { scope } }).then((r) => setData(r.data)).catch(() => toast.error("Carte indisponible")); }, [scope]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { window.addEventListener(RDV_CHANGED, load); return () => window.removeEventListener(RDV_CHANGED, load); }, [load]);
  const toggle = (id) => { setRoute(null); setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id])); };
  const geocodeAll = async () => { await api.post("/planning/geocode-all"); toast.success("Localisation de tous les dossiers lancée : rechargez la carte dans une minute"); };
  const points = (data?.points || []).filter((p) => matches(p, filters) || selected.includes(p.dossier_id));
  return (
    <div className="flex gap-4 h-[calc(100vh-260px)] min-h-[560px]" data-testid="planning-map-view">
      <div className="flex-1 flex flex-col gap-2 min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
          <div className="inline-flex p-0.5 rounded-md border border-norea-border bg-white" data-testid="map-scope">
            {[["a_planifier", "Interventions à planifier"], ["tous", "Tous les dossiers"]].map(([k, l]) => (
              <button key={k} onClick={() => setScope(k)} className={`px-3 py-1 rounded ${scope === k ? "bg-norea-primary text-white" : "text-norea-textMuted"}`} data-testid={`map-scope-${k}`}>{l}</button>))}
          </div>
          <div className="flex items-center gap-3 text-norea-textMuted" data-testid="map-legend">
            {LEGEND.map(([k, l]) => <span key={k} className="inline-flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: TYPE_COLOR[k] }} />{l}</span>)}
            <span className="inline-flex items-center gap-1"><i className="w-2.5 h-2.5 rounded-full inline-block bg-slate-700" />RDV prévu</span>
            {data && <span data-testid="map-count">{points.length} client{points.length > 1 ? "s" : ""}{data.sans_position ? ` · ${data.sans_position} sans position` : ""}</span>}
            {data?.a_geocoder > 0 && <button onClick={geocodeAll} className="btn-ghost !text-xs !py-0.5" data-testid="map-geocode-all"><MapPinOff className="w-3 h-3" />Localiser {data.a_geocoder} dossiers</button>}
          </div>
        </div>
        <div className="flex-1 relative card-flat overflow-hidden">
          {!data && <div className="absolute inset-0 z-[500] flex items-center justify-center bg-white/60"><Loader2 className="w-5 h-5 animate-spin" /></div>}
          <PlanningMap points={points} selected={selected} onToggle={toggle} route={route} onOpenRdv={onOpenRdv} />
        </div>
      </div>
      <TourneePanel opts={opts} points={data?.points || []} selected={selected} onClear={() => setSelected([])} onRemove={toggle} route={route} setRoute={setRoute} />
    </div>
  );
}
